import React from 'react';
import { TextEffect } from '@/customTypes/chatStory';
import { setCustomNameToText } from '@/service/ChapterService';
import { useAppSelector } from '@/store/hooks';
import { RootState } from '@/store/store';
import { css, SerializedStyles } from '@emotion/react';
import { useEffect, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';

interface ITextEffectOnImage {
  effect: TextEffect;
  size: {
    width: number;
    height: number;
  };
  originSize: {
    width: number;
    height: number;
  };
  startPoint?: {
    x: number;
    y: number;
  };
}

const TextEffectOnImage = ({
  effect,
  size,
  originSize,
  startPoint = { x: 0, y: 0 },
}: ITextEffectOnImage) => {
  const { playChapterInfo } = useAppSelector(
    (state: RootState) => state.chapter,
    shallowEqual,
  );

  const [textEffect, setTextEffect] = useState<SerializedStyles | undefined>(
    undefined,
  );

  const originRateRef = useRef<{
    left: number;
    top: number;
    right: number;
    bottom: number;
  }>({
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
  });

  useEffect(() => {
    originRateRef.current = {
      left: effect.leftTop[0] / originSize.width,
      top: effect.leftTop[1] / originSize.height,
      right: effect.rightTop[0] / originSize.width,
      bottom: effect.leftBottom[1] / originSize.height,
    };
  }, []);

  const checkCharacterType = (text: string) => {
    let koreanCount = 0;
    Array.from(text).forEach((char) => {
      if (char.match(/[\u3131-\u318E\uAC00-\uD7A3]+/)) {
        koreanCount++;
      }
    });
    return koreanCount > text.length / 2;
  };

  useEffect(() => {
    const left = startPoint.x + size.width * originRateRef.current.left;
    const top = startPoint.y + size.height * originRateRef.current.top;
    const right = startPoint.x + size.width * originRateRef.current.right;
    const bottom = startPoint.y + size.height * originRateRef.current.bottom;
    const rect = {
      left: left,
      top: top,
      width: right - left,
      height: bottom - top,
    };
    const customContent = setCustomNameToText({
      text: effect.content,
      playChapterInfo,
    });

    /*
     * 모든 수치는 임의의 수치로 계산되었음.
     * 1. MaxFontSize(px)를 기준으로 텍스트 영역을 계산한다.
     * 1-1. 문자의 세로, 가로 길이는 폰트 사이즈를 기준으로 한글과 영어를 구분하여 각각 가중치를 주어 계산한다.
     * 1-2. 텍스트 영역의 가로는 문자열의 개행을 고려하여 개행으로 나눈 문장 중 가장 긴 문장을 기준으로 계산한다.
     * 1-3. 텍스트 영역의 세로는 개행으로 나눈 문장의 개수(문장 배열의 길이)를 기준으로 계산한다.
     * 2. 구해진 텍스트 영역을 기준으로 이미지 영역을 나누어 비율을 계산한다.
     * 3. 구해진 비율을 기준으로 폰트 사이즈를 계산한다. 폰트 사이즈의 단위는 px다.
     */
    const isKorean = checkCharacterType(customContent);
    const splitContent = customContent.split(/\n/);
    const maxFontSize = 24;
    const charWidth = isKorean ? maxFontSize / 1.5 : maxFontSize / 2;
    const charHeight = isKorean ? maxFontSize * 1.5 : maxFontSize;

    const findLongest = splitContent.reduce((longest, item) => {
      return item.length > longest ? item.length : longest;
    }, 0);

    const textArea =
      findLongest * charWidth * (splitContent.length * charHeight);
    const rectArea = rect.width * rect.height;
    const areaRatio = rectArea / textArea;
    const fontSize = maxFontSize * (areaRatio > 1 ? 1 : areaRatio);

    const textEffect =
      effect &&
      rect &&
      css`
        position: absolute;
        left: ${rect.left}px;
        top: ${rect.top}px;
        width: ${rect.width}px;
        height: ${rect.height}px;
        text-align: ${effect.align};
        font-size: ${fontSize <= maxFontSize ? `${fontSize}px` : `1.5rem`};
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        word-break: break-all;
      `;

    textEffect && setTextEffect(textEffect);
  }, [size]);

  return (
    <>
      {textEffect && (
        <div css={textEffect}>
          {effect.contentWithEffect.map((item) => {
            return (
              <span
                key={item.text}
                css={css`
                  font-weight: ${item.style === 'bold' ||
                  item.style === 'italic_bold'
                    ? 'bold'
                    : 'normal'};
                  font-style: ${item.style === 'italic' ||
                  item.style === 'italic_bold'
                    ? 'italic'
                    : 'normal'};
                  color: ${item.color};
                `}
              >
                {setCustomNameToText({
                  text: item.text,
                  playChapterInfo,
                })}
              </span>
            );
          })}
        </div>
      )}
    </>
  );
};

export default TextEffectOnImage;
